import {
  REMINDER_GRADE_THRESHOLD,
  calculateEstimatedGrade,
  getPartServiceDate,
  monthsBetween,
} from "../service-reminder/grade-utils"
import { PartFunction } from "./part-function"
import { ServiceAction, ServicePartActionUtils } from "./service-part-action"
import { ACCEPTED_TASK_STATUSES, ApprovalStatus } from "./work-order"

/** What the cyclist said about the work recommended for a component. */
export enum PartCustomerDecision {
  APPROVED = "APPROVED",
  DENIED = "DENIED",
  PENDING = "PENDING",
  /** No answer exists, e.g. nothing was recommended or nothing was sent. */
  NOT_ASKED = "NOT_ASKED",
}

/** Why a component is worth a closer look when the bike comes back. */
export enum PartFocusReason {
  DENIED = "DENIED",
  WORN = "WORN",
}

export enum PartVisitAgeUnit {
  DAY = "DAY",
  WEEK = "WEEK",
  MONTH = "MONTH",
}

/** How long ago a visit was, in the unit that reads best. Never in years. */
export interface PartVisitAge {
  unit: PartVisitAgeUnit
  count: number
}

export interface PartVisitProduct {
  title: string
  quantity: number
}

/** A component as it was graded during an earlier job on the same bike. */
export interface PartVisitEvaluation {
  partId: string
  lifespanMonths: number
  grade: number | null
  action: ServiceAction
  comment: string | null
  partFunction: PartFunction
  products: PartVisitProduct[]
}

/** The work order item for a component, where `grade` is its grade before. */
export interface PartVisitTask extends PartVisitEvaluation {
  newGrade: number | null
  approvalStatus: ApprovalStatus
}

export interface PartVisitJob {
  jobId: string
  handInDate: Date
  properInspection: {
    createdAt: Date
    removedPartIds: string[]
    evaluations: PartVisitEvaluation[]
  } | null
  receptionEvaluations: PartVisitEvaluation[]
  tasks: PartVisitTask[]
}

/** What the mechanic can use from a component's last visit. */
export interface PartLastVisit {
  partId: string
  visitDate: string
  age: PartVisitAge
  /** False for a visit without an inspection, e.g. a quick job. */
  wasInspected: boolean
  gradeAfterVisit: number | null
  /** Null when there is no grade to wear down from, e.g. an adjustment. */
  predictedGrade: number | null
  recommendedAction: ServiceAction
  partFunction: PartFunction
  comment: string | null
  customerDecision: PartCustomerDecision
  /** Set when the mechanic should look closer. */
  focusReason: PartFocusReason | null
  products: PartVisitProduct[]
}

const MS_PER_DAY = 1000 * 60 * 60 * 24
const MAX_DAYS_SHOWN_IN_DAYS = 13
const MAX_DAYS_SHOWN_IN_WEEKS = 59

const toVisitAge = (visitDate: Date, now: Date): PartVisitAge => {
  const days = Math.max(
    0,
    Math.floor((now.getTime() - visitDate.getTime()) / MS_PER_DAY),
  )
  if (days <= MAX_DAYS_SHOWN_IN_DAYS) {
    return { unit: PartVisitAgeUnit.DAY, count: days }
  }
  if (days <= MAX_DAYS_SHOWN_IN_WEEKS) {
    return { unit: PartVisitAgeUnit.WEEK, count: Math.floor(days / 7) }
  }
  return {
    unit: PartVisitAgeUnit.MONTH,
    count: Math.round(monthsBetween(visitDate, now)),
  }
}

const toCustomerDecision = (
  status: ApprovalStatus | undefined,
): PartCustomerDecision => {
  switch (status) {
    case undefined:
      return PartCustomerDecision.NOT_ASKED
    case ApprovalStatus.ACCEPTED_VIA_RECEPTION:
    case ApprovalStatus.ACCEPTED_VIA_PROPER_INSPECTION:
    case ApprovalStatus.ACCEPTED_VIA_ADDON_ORDER:
      return PartCustomerDecision.APPROVED
    case ApprovalStatus.DENIED_VIA_PROPER_INSPECTION:
    case ApprovalStatus.DENIED_VIA_ADDON_ORDER:
      return PartCustomerDecision.DENIED
    case ApprovalStatus.PENDING_VIA_PROPER_INSPECTION:
    case ApprovalStatus.PENDING_VIA_ADDON_ORDER:
      return PartCustomerDecision.PENDING
  }
}

const toFocusReason = (
  recommendedAction: ServiceAction,
  customerDecision: PartCustomerDecision,
  predictedGrade: number | null,
): PartFocusReason | null => {
  // "Montera" is an optional extra, not a component to keep an eye on.
  if (ServicePartActionUtils.addsNewComponent(recommendedAction)) return null
  if (customerDecision === PartCustomerDecision.DENIED) {
    return PartFocusReason.DENIED
  }
  if (predictedGrade !== null && predictedGrade <= REMINDER_GRADE_THRESHOLD) {
    return PartFocusReason.WORN
  }
  return null
}

const toPartLastVisit = (
  job: PartVisitJob,
  visitDate: Date,
  record: PartVisitEvaluation,
  task: PartVisitTask | undefined,
  now: Date,
): PartLastVisit => {
  const recommendedAction = task?.action ?? record.action
  const isAccepted =
    task !== undefined && ACCEPTED_TASK_STATUSES.includes(task.approvalStatus)
  // A "Montera" row always holds a grade, but until the cyclist accepts the
  // work there is no component on the bike for it to describe.
  const isOnBike =
    isAccepted || !ServicePartActionUtils.addsNewComponent(recommendedAction)
  const gradeAtVisit = isOnBike ? (record.grade ?? task?.grade ?? null) : null
  // An addon item carries its new grade from the start, so only work the
  // cyclist accepted may count as a fresh component.
  const acceptedNewGrade = isAccepted ? task.newGrade : null
  const gradeAfterVisit = acceptedNewGrade ?? gradeAtVisit

  const predictedGrade =
    gradeAfterVisit === null
      ? null
      : calculateEstimatedGrade(
          gradeAfterVisit,
          record.lifespanMonths,
          visitDate,
          now,
        )
  const customerDecision = toCustomerDecision(task?.approvalStatus)

  return {
    partId: record.partId,
    visitDate: visitDate.toISOString(),
    age: toVisitAge(visitDate, now),
    wasInspected: job.properInspection !== null,
    gradeAfterVisit,
    predictedGrade,
    recommendedAction,
    partFunction: record.partFunction,
    comment: record.comment || task?.comment || null,
    customerDecision,
    focusReason: toFocusReason(
      recommendedAction,
      customerDecision,
      predictedGrade,
    ),
    products: task?.products.length ? task.products : record.products,
  }
}

const sortNewestFirst = (jobs: PartVisitJob[]): PartVisitJob[] =>
  [...jobs].sort(
    (a, b) => getPartServiceDate(b).getTime() - getPartServiceDate(a).getTime(),
  )

/** Declined work first, then whatever is expected to be most worn. */
const byMostUrgent = (a: PartLastVisit, b: PartLastVisit): number => {
  const isDenied = (visit: PartLastVisit) =>
    visit.focusReason === PartFocusReason.DENIED
  if (isDenied(a) !== isDenied(b)) return isDenied(a) ? -1 : 1
  return (a.predictedGrade ?? 11) - (b.predictedGrade ?? 11)
}

/**
 * The newest record per component across a bike's earlier jobs, so a component
 * left out last time still shows what an older visit found.
 */
export const buildPartLastVisits = (
  jobs: PartVisitJob[],
  now: Date = new Date(),
): PartLastVisit[] => {
  const visitsByPartId = new Map<string, PartLastVisit>()

  for (const job of sortNewestFirst(jobs)) {
    const visitDate = getPartServiceDate(job)
    const tasksByPartId = new Map(job.tasks.map((task) => [task.partId, task]))
    // Without an inspection the reception's untouched rows say nothing about
    // the component, and must not hide a real grade from an older visit.
    const evaluations =
      job.properInspection?.evaluations ??
      job.receptionEvaluations.filter(
        (evaluation) => evaluation.action !== ServiceAction.DO_NOTHING,
      )

    const records = [
      ...evaluations,
      // Work that never had an evaluated row, e.g. an addon order
      ...job.tasks.filter(
        (task) =>
          !evaluations.some((evaluation) => evaluation.partId === task.partId),
      ),
    ]

    for (const record of records) {
      if (visitsByPartId.has(record.partId)) continue
      visitsByPartId.set(
        record.partId,
        toPartLastVisit(
          job,
          visitDate,
          record,
          tasksByPartId.get(record.partId),
          now,
        ),
      )
    }
  }

  return [...visitsByPartId.values()].sort(byMostUrgent)
}

/** When the bike was last here, or null on its first visit. */
export const findLastVisitDate = (jobs: PartVisitJob[]): string | null => {
  const [lastJob] = sortNewestFirst(jobs)
  return lastJob ? getPartServiceDate(lastJob).toISOString() : null
}

/** What the mechanic removed at the bike's most recent inspection. */
export const findLastRemovedPartIds = (jobs: PartVisitJob[]): string[] => {
  const lastInspectedJob = sortNewestFirst(jobs).find(
    (job) => job.properInspection !== null,
  )
  return lastInspectedJob?.properInspection?.removedPartIds ?? []
}
